/**
 * Every call the web app can make, typed, in one list. Nothing outside this file
 * knows a URL.
 */
import {
  type Beoordeling,
  bestemmingsplanUrl,
  budgetGevolgSchema,
  bulkUitslagSchema,
  buurtenKaartSchema,
  datasetVerwijderdSchema,
  dossierSchema,
  type Fase,
  type FotoLabel,
  type FundaVenster,
  fotoLijstSchema,
  fotoStandSchema,
  fundaBlokkadeSchema,
  fundaRustSchema,
  gebiedSchema,
  type LabelfeedKlassen,
  type Labelgroep,
  labelfeedSchema,
  labelOverzichtSchema,
  leesBestemmingsplanTekst,
  listingSchema,
  logboekSchema,
  type NieuweRealworksZoekopdrachten,
  type NieuweZoekopdrachtUitGebieden,
  type PushAbonnementInvoer,
  postcodePuntenSchema,
  pushSleutelSchema,
  pushUitslagSchema,
  realworksZoekopdrachtenSchema,
  realworksZoekopdrachtSchema,
  sportcityOverzichtSchema,
  type TaxatieInvoer,
  taxatieMetUitkomstSchema,
  transactieDetailSchema,
  transactiePuntenSchema,
  type VangerInstellingen,
  type VangerInvoer,
  vangerStandSchema,
  wijkcijfersSchema,
  type Zoekfilters,
  type ZoekopdrachtBewerking,
  zoekfiltersNaarQuery,
  zoekgebiedSchattingSchema,
  zoekgebiedStandSchema,
  zoekgebiedUitslagSchema,
  zoekopdrachtSchema,
} from '@mamimap/shared';
import { z } from 'zod';
import type { ServerFilter, Sortering } from '../filter-url';
import { isLokaleSortering, SORTERING_STANDAARD } from '../filter-url';
import { type QueryWaarde, verzoek } from './client';
import {
  datasetsSchema,
  facettenSchema,
  importResultaatSchema,
  sessieSchema,
  soepelePagina,
  soepeleWoningPunten,
} from './schemas';

/** Eén rij die niet door het contract komt kost die rij, niet de hele tabel. */
const gepagineerdeWoningen = soepelePagina(listingSchema);

/** The filter as query parameters; arrays are joined by the client. */
function params(filter: ServerFilter): Record<string, QueryWaarde> {
  return {
    plaatsen: filter.plaatsen,
    buurten: filter.buurten,
    datasets: filter.datasets,
    prijsMin: filter.prijsMin,
    prijsMax: filter.prijsMax,
    m2Min: filter.m2Min,
    m2Max: filter.m2Max,
    energielabels: filter.energielabels,
    beoordelingen: filter.beoordelingen,
    aanbod: filter.aanbod,
    // Alleen als hij aanstaat: `zonderLabel=false` is geen filter, maar zou wel
    // in de URL en in de query-sleutel gaan staan.
    zonderLabel: filter.zonderLabel === true ? 'true' : undefined,
    kamersMin: filter.kamersMin,
    kamersMax: filter.kamersMax,
    zoekterm: filter.zoekterm,
  };
}

/** Wat de labelfeed moet tonen; de kaartlaag kiest zelf klassen en m² (MA-61). */
export type LabelfeedVraag = {
  dagen: number;
  klassen?: LabelfeedKlassen;
  groepen?: readonly Labelgroep[];
  minM2?: number | null;
  maxM2?: number | null;
};

/** A label sort has no server equivalent; the rows come back in a fixed order. */
function serverSortering(sortering: Sortering): string {
  return isLokaleSortering(sortering) ? SORTERING_STANDAARD : sortering;
}

export const api = {
  /** GET /api/v1/woningen/:id - één woning, voor het doelpand van een taxatie (MA-56). */
  woning: (id: string) => verzoek(`/woningen/${id}`, listingSchema),

  /** GET /api/v1/transacties - verkochte woningen rond een punt (MA-56). */
  transacties: (invoer: { lat: number; lng: number; straalM: number }) =>
    verzoek('/transacties', transactiePuntenSchema, { params: invoer }),

  /** GET /api/v1/transacties/:id - alles van één verkochte woning. */
  transactie: (id: string) => verzoek(`/transacties/${id}`, transactieDetailSchema),

  /** PUT /api/v1/transacties/:id/fotos/:volgnummer/label - wat de foto laat zien. */
  zetFotoLabel: (invoer: { id: string; volgnummer: number; label: FotoLabel | null }) =>
    verzoek(`/transacties/${invoer.id}/fotos/${invoer.volgnummer}/label`, z.null(), {
      methode: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label: invoer.label }),
    }),

  /** GET /api/v1/woningen/:id/dossier - wat de open bronnen over de woning zeggen (MA-56). */
  dossier: (woningId: string, vernieuw = false) =>
    verzoek(`/woningen/${woningId}/dossier`, dossierSchema, {
      params: vernieuw ? { vernieuw: '1' } : undefined,
    }),

  /** GET /api/v1/realworks/postcodes - de postcodes rond het doelpand, uit de BAG (MA-66). */
  realworksPostcodes: (invoer: { lat: number; lng: number; straalM: number }) =>
    verzoek('/realworks/postcodes', postcodePuntenSchema, { params: invoer }),

  /** GET /api/v1/realworks/zoekopdrachten - de zoekopdrachten van één woning (MA-66). */
  realworksZoekopdrachten: (woningId: string) =>
    verzoek('/realworks/zoekopdrachten', realworksZoekopdrachtenSchema, {
      params: { woning: woningId },
    }),

  /** POST /api/v1/realworks/zoekopdrachten - klaarzetten voor de extensie (MA-66). */
  maakRealworksZoekopdrachten: (invoer: NieuweRealworksZoekopdrachten) =>
    verzoek('/realworks/zoekopdrachten', realworksZoekopdrachtenSchema, {
      methode: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(invoer),
    }),

  /** POST /api/v1/realworks/zoekopdrachten/:id/opnieuw - nog een keer laten draaien. */
  heropenRealworksZoekopdracht: (id: string) =>
    verzoek(`/realworks/zoekopdrachten/${id}/opnieuw`, realworksZoekopdrachtSchema, {
      methode: 'POST',
    }),

  /** DELETE /api/v1/realworks/zoekopdrachten/:id */
  verwijderRealworksZoekopdracht: (id: string) =>
    verzoek(`/realworks/zoekopdrachten/${id}`, z.null(), { methode: 'DELETE' }),

  /** GET /api/v1/woningen/:id/taxatie - de taxatie en haar uitkomst, of twee keer null. */
  taxatie: (woningId: string) => verzoek(`/woningen/${woningId}/taxatie`, taxatieMetUitkomstSchema),

  /** PUT /api/v1/woningen/:id/taxatie - het hele document; de API rekent de uitkomst. */
  bewaarTaxatie: (invoer: { woningId: string; taxatie: TaxatieInvoer }) =>
    verzoek(`/woningen/${invoer.woningId}/taxatie`, taxatieMetUitkomstSchema, {
      methode: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(invoer.taxatie),
    }),

  /** GET /api/v1/woningen - one keyset page of the table. */
  woningen: (invoer: {
    filter: ServerFilter;
    sortering: Sortering;
    limiet: number;
    cursor?: string;
  }) =>
    verzoek('/woningen', gepagineerdeWoningen, {
      params: {
        ...params(invoer.filter),
        sortering: serverSortering(invoer.sortering),
        limiet: invoer.limiet,
        cursor: invoer.cursor,
      },
    }),

  /** GET /api/v1/woningen/geojson - the map's points, without the ones lacking coordinates. */
  geojson: (filter: ServerFilter) =>
    verzoek('/woningen/geojson', soepeleWoningPunten, { params: params(filter) }),

  /** GET /api/v1/woningen/:id/punt - één woning als kaartpunt, los van het filter (MA-59). */
  punt: (id: string) => verzoek(`/woningen/${encodeURIComponent(id)}/punt`, soepeleWoningPunten),

  /** GET /api/v1/woningen/facetten - the counts in the filter bar. */
  facetten: (filter: ServerFilter) =>
    verzoek('/woningen/facetten', facettenSchema, { params: params(filter) }),

  /**
   * Het bereik dat er binnen dit filter werkelijk in zit, als hint in de van/tot-
   * velden. Vier keer één rij: de goedkoopste, de duurste, de kleinste en de
   * grootste. Elk criterium telt zonder zichzelf mee - net als de facetten -
   * anders zou het eigen filter de hint dichtknijpen tot wat je al had ingevuld.
   * Een filter op 80-120 m² gaf '0 woningen' terwijl de hele set tussen 50 en 70
   * ligt, en niets op het scherm verklaarde dat.
   */
  bereik: async (filter: ServerFilter): Promise<Bereik> => {
    const eenRij = (deel: ServerFilter, sortering: Sortering) =>
      verzoek('/woningen', gepagineerdeWoningen, {
        params: { ...params(deel), sortering: serverSortering(sortering), limiet: 1 },
      });
    const zonderPrijs = { ...filter, prijsMin: undefined, prijsMax: undefined };
    const zonderM2 = { ...filter, m2Min: undefined, m2Max: undefined };
    const [prijsLaag, prijsHoog, m2Laag, m2Hoog] = await Promise.all([
      eenRij(zonderPrijs, 'prijs_op'),
      eenRij(zonderPrijs, 'prijs_af'),
      eenRij(zonderM2, 'oppervlakte_op'),
      eenRij(zonderM2, 'oppervlakte_af'),
    ]);
    return {
      prijsMin: prijsLaag.data[0]?.vraagprijs ?? null,
      prijsMax: prijsHoog.data[0]?.vraagprijs ?? null,
      m2Min: m2Laag.data[0]?.oppervlakteM2 ?? null,
      m2Max: m2Hoog.data[0]?.oppervlakteM2 ?? null,
    };
  },

  /**
   * PUT /api/v1/woningen/:id/beoordeling - ons oordeel over één woning.
   *
   * De enige schrijfactie op een woning. Het antwoord is de bijgewerkte woning,
   * dus de schermen hoeven er niets bij op te halen om de nieuwe stand te tonen.
   */
  beoordeel: (invoer: { id: string; beoordeling: Beoordeling }) =>
    verzoek(`/woningen/${invoer.id}/beoordeling`, listingSchema, {
      methode: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ beoordeling: invoer.beoordeling }),
    }),

  /** PUT /api/v1/woningen/:id/fase - de kolom van een kansje op het bord (MA-51). */
  zetFase: (invoer: { id: string; fase: Fase; volgorde?: number }) =>
    verzoek(`/woningen/${invoer.id}/fase`, listingSchema, {
      methode: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fase: invoer.fase, volgorde: invoer.volgorde }),
    }),

  /** PUT /api/v1/woningen/:id/hoofdfoto - de foto die wij de beste vinden (MA-51). */
  zetHoofdfoto: (invoer: { id: string; volgnummer: number | null }) =>
    verzoek(`/woningen/${invoer.id}/hoofdfoto`, listingSchema, {
      methode: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ volgnummer: invoer.volgnummer }),
    }),

  /**
   * PATCH /api/v1/woningen/beoordeling - één oordeel over een groep woningen.
   * Dezelfde regels als per woning; alle rijen krijgen dezelfde keurdatum.
   */
  beoordeelBulk: (invoer: { ids: readonly string[]; beoordeling: Beoordeling }) =>
    verzoek('/woningen/beoordeling', bulkUitslagSchema, {
      methode: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: invoer.ids, beoordeling: invoer.beoordeling }),
    }),

  /** DELETE /api/v1/woningen - een groep woningen weghalen. Onomkeerbaar binnen de app. */
  verwijderWoningen: (ids: readonly string[]) =>
    verzoek('/woningen', bulkUitslagSchema, {
      methode: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    }),

  /** DELETE /api/v1/datasets/:id - de dataset en de woningen die er nu aan hangen. */
  verwijderDataset: (id: string) =>
    verzoek(`/datasets/${id}`, datasetVerwijderdSchema, { methode: 'DELETE' }),

  /** GET /api/v1/datasets - every import, newest first. */
  datasets: () => verzoek('/datasets', datasetsSchema),

  /** POST /api/v1/datasets/import - a CSV, XLSX or JSON file plus the portal it came from. */
  importeer: (invoer: { bestand: File; bron: string }) => {
    const formulier = new FormData();
    formulier.append('bestand', invoer.bestand);
    formulier.append('bron', invoer.bron);
    formulier.append('bestandsnaam', invoer.bestand.name);
    return verzoek('/datasets/import', importResultaatSchema, {
      methode: 'POST',
      body: formulier,
    });
  },

  /**
   * GET /api/v1/beheer/zoekopdrachten - wat de extensie periodiek afloopt, met per
   * zoekopdracht de laatste ronde en hoeveel woningen de feed nu telt.
   */
  zoekopdrachten: () => verzoek('/beheer/zoekopdrachten', z.array(zoekopdrachtSchema)),

  /** GET /api/v1/beheer/funda-rust - de globale rust na een blokkade en het dagtotaal (MA-28). */
  fundaRust: () => verzoek('/beheer/funda-rust', fundaRustSchema),

  /** PUT /api/v1/beheer/funda-dagplafond - het dagplafond bijstellen (MA-32); geeft de stand terug. */
  zetFundaDagplafond: (dagplafond: number) =>
    verzoek('/beheer/funda-dagplafond', fundaRustSchema, {
      methode: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dagplafond }),
    }),

  /** PUT /api/v1/beheer/funda-venster - de vaste tijden en het budget per keer (MA-46). */
  zetFundaVenster: (venster: FundaVenster) =>
    verzoek('/beheer/funda-venster', fundaRustSchema, {
      methode: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(venster),
    }),

  /** GET /api/v1/beheer/funda-blokkades - het blokkadelog, nieuwste eerst (MA-46). */
  fundaBlokkades: () => verzoek('/beheer/funda-blokkades', z.array(fundaBlokkadeSchema)),

  /** POST /api/v1/beheer/zoekopdrachten - een nieuwe zoekopdracht uit gekozen gebieden. */
  maakZoekopdracht: (invoer: NieuweZoekopdrachtUitGebieden) =>
    verzoek('/beheer/zoekopdrachten', zoekopdrachtSchema, {
      methode: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(invoer),
    }),

  /** PATCH /api/v1/beheer/zoekopdrachten/:id - alleen wat anders is gaat mee. */
  bewerkZoekopdracht: (invoer: { id: string; wijziging: ZoekopdrachtBewerking }) =>
    verzoek(`/beheer/zoekopdrachten/${invoer.id}`, zoekopdrachtSchema, {
      methode: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(invoer.wijziging),
    }),

  /** GET /api/v1/beheer/gebieden - Funda's eigen gebiedenlijst, voor de buurtkiezer. */
  gebieden: () => verzoek('/beheer/gebieden', z.array(gebiedSchema)),

  /** GET /api/v1/beheer/buurten - de CBS-buurten als GeoJSON, met hun postcodes. */
  buurten: () => verzoek('/beheer/buurten', buurtenKaartSchema),

  /** GET /api/v1/labels/feed - nieuwe labels in het zoekgebied, nieuwste eerst (MA-61). */
  labelfeed: (vraag: LabelfeedVraag) =>
    verzoek('/labels/feed', labelfeedSchema, {
      params: {
        dagen: String(vraag.dagen),
        klassen: vraag.klassen ?? 'zoekfilter',
        groepen: vraag.groepen ? vraag.groepen.join(',') : undefined,
        minM2: vraag.minM2 == null ? undefined : String(vraag.minM2),
        maxM2: vraag.maxM2 == null ? undefined : String(vraag.maxM2),
      },
    }),

  /** PUT /api/v1/labels/:id/afgekeurd - het label voor iedereen van de kaart en uit de feed. */
  keurLabelAf: (id: number) => verzoek(`/labels/${id}/afgekeurd`, z.null(), { methode: 'PUT' }),

  /** GET /api/v1/labels/overzicht - labels per buurt en per dag, en wat er te koop kwam. */
  labelOverzicht: () => verzoek('/labels/overzicht', labelOverzichtSchema),

  /** GET /api/v1/beheer/zoekgebied - welke buurten aan staan, en de schatting per postcode. */
  zoekgebied: () => verzoek('/beheer/zoekgebied', zoekgebiedStandSchema),

  /**
   * PUT /api/v1/beheer/zoekgebied - de keuze en de zoekfilters opslaan; de
   * zoekopdrachten volgen, met de filters in hun URL.
   */
  zetZoekgebied: (invoer: { buurtcodes: readonly string[]; filters: Zoekfilters }) =>
    verzoek('/beheer/zoekgebied', zoekgebiedUitslagSchema, {
      methode: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(invoer),
    }),

  /** GET /api/v1/beheer/zoekgebied/schatting - de schatting voor nog niet opgeslagen filters. */
  zoekgebiedSchatting: (filters: Zoekfilters) =>
    verzoek('/beheer/zoekgebied/schatting', zoekgebiedSchattingSchema, {
      params: zoekfiltersNaarQuery(filters),
    }),

  /** GET /api/v1/beheer/zoekgebied/budget-gevolg - wat er door het budget in deze filters uit de zoekopdrachten valt. */
  budgetGevolg: (filters: Zoekfilters) =>
    verzoek('/beheer/zoekgebied/budget-gevolg', budgetGevolgSchema, {
      params: zoekfiltersNaarQuery(filters),
    }),

  /** PUT /api/v1/beheer/zoekgebied/grens - boven hoeveel pagina's per ronde het scherm waarschuwt. */
  zetZoekgebiedGrens: (grensPaginas: number) =>
    verzoek('/beheer/zoekgebied/grens', zoekgebiedStandSchema, {
      methode: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ grensPaginas }),
    }),

  /** GET /api/v1/woningen/:id/fotos - welke foto's een woning heeft; het popupje bladert erdoor. */
  fotos: (id: string) => verzoek(`/woningen/${id}/fotos`, fotoLijstSchema),

  /** GET /api/v1/woningen/:id/logboek - actief, weg, terug, met de prijs van elk moment. */
  logboek: (id: string) => verzoek(`/woningen/${id}/logboek`, logboekSchema),

  /**
   * Het bestemmingsplan op een punt (MA-50). De enige aanroep hier die niet naar
   * de eigen API gaat: PDOK staat open voor de browser en in de CSP, en er valt
   * niets te bewaren - dus geen omweg via de server en geen cookie mee.
   */
  bestemmingsplan: async (lng: number, lat: number) => {
    const antwoord = await fetch(bestemmingsplanUrl(lng, lat), { credentials: 'omit' });
    if (!antwoord.ok) throw new Error(`PDOK gaf ${antwoord.status}`);
    return leesBestemmingsplanTekst(await antwoord.text());
  },

  /** GET /api/v1/fotos/stand - op schijf, nog te halen, opgegeven, en het budget. */
  fotoStand: () => verzoek('/fotos/stand', fotoStandSchema),

  /** GET /api/v1/sportcity/overzicht - clubs en plaatsen, alleen in de werkruimte SportCity (MA-72). */
  sportcityOverzicht: () => verzoek('/sportcity/overzicht', sportcityOverzichtSchema),

  /** GET /api/v1/sportcity/wijken/GM0363 - wijken en buurten met de CBS-cijfers (MA-73). */
  sportcityWijken: (gemeentecode: string) =>
    verzoek(`/sportcity/wijken/${encodeURIComponent(gemeentecode)}`, wijkcijfersSchema),

  /** GET /api/v1/auth/sessie - altijd 200; `ingelogd` zegt of er een cookie was. */
  sessie: () => verzoek('/auth/sessie', sessieSchema),

  /** POST /api/v1/auth/inloggen - zet de sessiecookie en geeft dezelfde vorm terug. */
  inloggen: (invoer: { email: string; wachtwoord: string }) =>
    verzoek('/auth/inloggen', sessieSchema, {
      methode: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: invoer.email, wachtwoord: invoer.wachtwoord }),
    }),

  /** POST /api/v1/auth/uitloggen - beëindigt de sessie en wist de cookie. */
  uitloggen: () =>
    verzoek('/auth/uitloggen', z.object({ uitgelogd: z.boolean() }), {
      methode: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),

  /* --- de vanger op de server en de meldingen -------------------------- */

  /** GET /api/v1/vanger/stand - slaapt, loopt, of wacht op een captcha. */
  vangerStand: () => verzoek('/vanger/stand', vangerStandSchema),

  /** POST /api/v1/vanger/start - een ronde starten; doet niets als er al een loopt. */
  vangerStart: () =>
    verzoek('/vanger/start', vangerStandSchema, {
      methode: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),

  /** POST /api/v1/vanger/stop - de lopende ronde afbreken. */
  vangerStop: () =>
    verzoek('/vanger/stop', vangerStandSchema, {
      methode: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),

  /** POST /api/v1/vanger/invoer - een tik, veeg of toets naar de servertab. */
  vangerInvoer: (invoer: VangerInvoer) =>
    verzoek('/vanger/invoer', z.unknown(), {
      methode: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(invoer),
    }),

  /** PATCH /api/v1/vanger/instellingen - het tijdstip van de ochtendmelding. */
  vangerInstellingen: (instellingen: VangerInstellingen) =>
    verzoek('/vanger/instellingen', vangerStandSchema, {
      methode: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(instellingen),
    }),

  /** GET /api/v1/push/sleutel - de publieke VAPID-sleutel, of null. */
  pushSleutel: () => verzoek('/push/sleutel', pushSleutelSchema),

  /** GET /api/v1/kaart/3d - de Google-sleutel voor het 3D-paneel, of null als die hier niet is ingesteld. */
  drieDSleutel: () => verzoek('/kaart/3d', z.object({ sleutel: z.string().nullable() })),

  pushAbonneer: (abonnement: PushAbonnementInvoer) =>
    verzoek('/push/abonnement', z.object({ geabonneerd: z.boolean() }), {
      methode: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(abonnement),
    }),

  pushAfmelden: (endpoint: string) =>
    verzoek('/push/afmelden', z.object({ geabonneerd: z.boolean() }), {
      methode: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint }),
    }),

  pushStand: (endpoint: string) =>
    verzoek('/push/stand', z.object({ geabonneerd: z.boolean() }), {
      methode: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint }),
    }),

  pushTest: () =>
    verzoek('/push/test', pushUitslagSchema, {
      methode: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),
};

/** Wat er binnen het huidige filter werkelijk in de data zit. */
export type Bereik = {
  prijsMin: number | null;
  prijsMax: number | null;
  m2Min: number | null;
  m2Max: number | null;
};

export type Api = typeof api;
